import { computed, makeObservable, observable } from 'mobx';

import type { Drawer } from './drawer';

interface PageTitle {
  label: string;
  href?: string;
}

type StoreAction = (...args: any) => any | undefined;

class UIStore {
  private _pageTitle?: PageTitle;
  private _contextDrawer: Drawer | undefined;
  private _showBack: boolean;
  private _action: StoreAction | undefined;
  private _isDrawerOpen = false;
  private _isTaskPanelOpen = false;
  private _isDataFetching = false;
  private _isPropertiesPanelOpen = false;

  taskPanelTargetId: string | null = null;

  currentLocation: string;
  propertiesPanelTargetId: string | null = null;

  /**
   * goBack can be overriden by screens to allow for going back in the hierarchy.
   */
  goBack: (() => void) | null = null;

  constructor() {
    makeObservable<
      UIStore,
      | '_pageTitle'
      | '_contextDrawer'
      | '_showBack'
      | '_action'
      | '_isDrawerOpen'
      | '_isTaskPanelOpen'
      | '_isDataFetching'
      | '_isPropertiesPanelOpen'
    >(this, {
      _pageTitle: observable,
      _contextDrawer: observable,
      _showBack: observable,
      _action: observable,
      _isDrawerOpen: observable,
      _isTaskPanelOpen: observable,
      _isDataFetching: observable,
      _isPropertiesPanelOpen: observable,
      taskPanelTargetId: observable,
      currentLocation: observable,
      isDataFetching: computed,
      isDrawerOpen: computed,
      pageTitle: computed,
      contextDrawer: computed,
      showBack: computed,
      isTaskPanelOpen: computed,
      action: computed,
    });
  }

  set pageTitle(title: PageTitle | undefined) {
    this._pageTitle = title;
  }

  get pageTitle(): PageTitle | undefined {
    return this._pageTitle;
  }

  get contextDrawer(): Drawer | undefined {
    return this._contextDrawer;
  }

  set contextDrawer(value: Drawer | undefined) {
    if (this._contextDrawer) {
      console.warn('A drawer is already registered on this screen.');
    }

    this._contextDrawer = value;
  }

  get isDrawerOpen(): boolean {
    return this._isDrawerOpen;
  }

  set isDrawerOpen(value: boolean) {
    this._isDrawerOpen = value;
  }

  set showBack(value: boolean) {
    this._showBack = value;
  }

  get showBack(): boolean {
    return this._showBack;
  }

  set action(value: StoreAction | undefined) {
    this._action = value;
  }

  get action(): StoreAction | undefined {
    return this._action;
  }

  set isDataFetching(value: boolean) {
    this._isDataFetching = value;
  }

  get isDataFetching(): boolean {
    return this._isDataFetching;
  }

  get isTaskPanelOpen(): boolean {
    return this._isTaskPanelOpen;
  }

  get isPropertiesPanelOpen(): boolean {
    return this._isPropertiesPanelOpen;
  }

  openTaskPanel(targetId: string) {
    this._isTaskPanelOpen = true;
    this.taskPanelTargetId = targetId;
  }

  closeTaskPanel() {
    this._isTaskPanelOpen = false;
    this.taskPanelTargetId = null;
  }

  openPropertiesPanel(targetId: string) {
    this._isPropertiesPanelOpen = true;
    this.propertiesPanelTargetId = targetId;
  }

  closePropertiesPanel() {
    this._isPropertiesPanelOpen = false;
    this.propertiesPanelTargetId = null;
  }
}

export const uiStore = new UIStore();
